import React, { useState } from 'react';
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	Input,
	VStack,
	Text,
	InputGroup,
	InputRightElement,
	Link,
	Spinner,
	useToast,
} from '@chakra-ui/react';
import { FaSignInAlt } from 'react-icons/fa';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { useAuthenticatedUserInfo } from '../../../services/Contexts/AuthenticatedUserInfoContext';
import {
	useAuthFlow,
	AUTH_MODES,
} from '../../../services/Contexts/AuthFlowContext';
import { getPortalSubdomain } from '../../../utils/subdomainUtils';
import {
	authFormInputProps,
	authFormLabelProps,
	authFormLinkProps,
	authFormMutedTextProps,
	authFormPrimaryButtonProps,
	authFormShellProps,
} from './authFormTheme';

const Login = ({ showTitle = true }) => {
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const { login, loading } = useAuthenticatedUserInfo();
	const { setMode } = useAuthFlow();
	const toast = useToast();

	const currentSubdomain = getPortalSubdomain(window?.location?.hostname || '');
	const path = window?.location?.pathname || '';
	const isAuthRoute = path === '/auth' || path === '/auth/';
	// Allow "Create one" on /signup (including ?mode=signin) and hide on portal /auth hosts.
	const canShowSignUp = !currentSubdomain && !isAuthRoute;

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			await login({ email, password });
		} catch (err) {
			const errorMessage =
				err?.response?.data?.msg || err?.message || 'Invalid email or password.';
			toast({
				title: 'Login failed',
				description: errorMessage,
				status: 'error',
				duration: 5000,
				isClosable: true,
			});
		}
	};

	return (
		<Box {...authFormShellProps}>
			{loading ? (
				<Box textAlign='center' py={10}>
					<Spinner size='xl' color='teal.500' thickness='3px' />
				</Box>
			) : (
				<form onSubmit={handleSubmit}>
					<VStack spacing={5} align='stretch'>
						{showTitle && (
							<Text fontSize='xl' fontWeight='bold' color='gray.800' textAlign='center'>
								Sign In
							</Text>
						)}

						<FormControl isRequired>
							<FormLabel {...authFormLabelProps}>Email</FormLabel>
							<Input
								type='email'
								placeholder='john.smith@acmecorp.com'
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								{...authFormInputProps}
							/>
						</FormControl>

						<FormControl isRequired>
							<FormLabel {...authFormLabelProps}>Password</FormLabel>
							<InputGroup>
								<Input
									type={showPassword ? 'text' : 'password'}
									placeholder='Enter your password'
									value={password}
									onChange={(e) => setPassword(e.target.value)}
									pr='3rem'
									{...authFormInputProps}
								/>
								<InputRightElement h='full'>
									<Button
										size='sm'
										variant='ghost'
										color='gray.500'
										onClick={() => setShowPassword((show) => !show)}
										tabIndex={-1}
										aria-label={showPassword ? 'Hide password' : 'Show password'}>
										{showPassword ? <ViewOffIcon /> : <ViewIcon />}
									</Button>
								</InputRightElement>
							</InputGroup>
						</FormControl>

						<Link
							{...authFormLinkProps}
							alignSelf='flex-end'
							fontSize='sm'
							cursor='pointer'
							onClick={() => setMode(AUTH_MODES.FORGOT_PASSWORD)}>
							Forgot password?
						</Link>

						<Button
							type='submit'
							rightIcon={<FaSignInAlt />}
							{...authFormPrimaryButtonProps}>
							Sign in
						</Button>

						{canShowSignUp && (
							<Text {...authFormMutedTextProps} mt={2}>
								Don&apos;t have an account?{' '}
								<Link
									{...authFormLinkProps}
									cursor='pointer'
									onClick={() => setMode(AUTH_MODES.SIGN_UP)}>
									Create one
								</Link>
							</Text>
						)}
					</VStack>
				</form>
			)}
		</Box>
	);
};

export default Login;
